import React from "react";
import { useCurrentFrame, interpolate } from "remotion";
import type { CityBubble } from "../../types/stats";

type BubblePlotProps = {
  bubbles: CityBubble[];
  colorMap: Map<string, string>;
  outlierCities: Set<string>;
  many: boolean;
};

const BubblePlot: React.FC<BubblePlotProps> = ({
  bubbles,
  colorMap,
  outlierCities,
  many,
}) => {
  const frame = useCurrentFrame();
  const radius = many ? 7 : 12;

  // 外れ値リングのパルスアニメーション (2秒周期)
  const pulse = interpolate(frame % 60, [0, 30, 60], [0, 1, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  return (
    <g>
      {/* 放射グラデーション定義 */}
      <defs>
        {bubbles.map(({ city }) => {
          const color = colorMap.get(city) ?? "#888";
          return (
            <radialGradient key={`grad-${city}`} id={`bubble-grad-${city}`}>
              <stop offset="0%" stopColor="#fff" stopOpacity={0.9} />
              <stop offset="30%" stopColor={color} stopOpacity={0.8} />
              <stop offset="100%" stopColor={color} stopOpacity={0.3} />
            </radialGradient>
          );
        })}
      </defs>

      {bubbles.map(({ city, x, y }) => {
        const color = colorMap.get(city) ?? "#888";
        const showLabel = outlierCities.has(city);

        return (
          <g key={city}>
            {/* ソフトグロー (大) */}
            <circle
              cx={x}
              cy={y}
              r={radius * 2.5}
              fill={color}
              opacity={0.08}
            />
            {/* ソフトグロー (中) */}
            <circle
              cx={x}
              cy={y}
              r={radius + 6}
              fill={color}
              opacity={0.15}
            />

            {/* 外れ値パルスリング */}
            {showLabel && (
              <circle
                cx={x}
                cy={y}
                r={radius + 4 + pulse * 6}
                fill="none"
                stroke={color}
                strokeWidth={1.5}
                opacity={0.4 * (1 - pulse)}
              />
            )}

            {/* メインバブル (グラデーション塗り) */}
            <circle
              cx={x}
              cy={y}
              r={radius}
              fill={`url(#bubble-grad-${city})`}
              stroke={color}
              strokeWidth={many ? 1 : 2}
              strokeOpacity={0.8}
            />

            {/* ハイライト (左上の光沢) */}
            <circle
              cx={x - radius * 0.3}
              cy={y - radius * 0.3}
              r={radius * 0.3}
              fill="#fff"
              opacity={0.4}
            />

            {/* 外れ値ラベル */}
            {showLabel && (
              <>
                {/* テキスト影 */}
                <text
                  x={x + radius + 8}
                  y={y - radius - 4}
                  fill="#000"
                  fontSize={many ? 23 : 29}
                  fontWeight={600}
                  opacity={0.5}
                  dx={1}
                  dy={1}
                >
                  {city}
                </text>
                <text
                  x={x + radius + 8}
                  y={y - radius - 4}
                  fill={color}
                  fontSize={many ? 23 : 29}
                  fontWeight={600}
                >
                  {city}
                </text>
              </>
            )}
          </g>
        );
      })}
    </g>
  );
};

export default BubblePlot;
